"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { 
  Upload, 
  FileSpreadsheet, 
  Eye, 
  Download,
  Calendar,
  DollarSign,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Info,
  CheckCircle,
  AlertCircle,
  PiggyBank,
  CreditCard,
  Wallet,
  ArrowUpCircle,
  ArrowDownCircle,
  Activity
} from "lucide-react"
import { Button } from "../../../components/ui/button"
import { Badge } from "../../../components/ui/badge"
import { useToast } from "../../../components/ui/use-toast"
import { Alert, AlertDescription } from "../../../components/ui/alert"
import BulkUploadModal from "../../Inventory/components/BulkUploadModal"

export default function CashFlowConfigurationModal({ clientId, onClose, embedded = false }) {
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [cashFlowData, setCashFlowData] = useState([])
  const [loading, setLoading] = useState(false)
  const [analysisData, setAnalysisData] = useState(null)

  const { toast } = useToast()

  // Datos de demostración para mostrar el formato esperado
  const demoData = [
    {
      concepto: "Ventas de Productos Textiles",
      enero: 1200000000,
      febrero: 1150000000,
      marzo: 1300000000,
      abril: 1180000000,
      mayo: 1250000000,
      junio: 1400000000,
      julio: 1350000000,
      agosto: 1450000000,
      septiembre: 1500000000,
      octubre: 1600000000,
      noviembre: 1700000000,
      diciembre: 1800000000,
      categoria: "Ingresos",
      tipo: "Operacional"
    },
    {
      concepto: "Servicios de Diseño",
      enero: 180000000,
      febrero: 170000000,
      marzo: 200000000,
      abril: 185000000,
      mayo: 195000000,
      junio: 220000000,
      julio: 210000000,
      agosto: 230000000,
      septiembre: 240000000,
      octubre: 250000000,
      noviembre: 260000000,
      diciembre: 280000000,
      categoria: "Ingresos",
      tipo: "Servicios"
    },
    {
      concepto: "Pago a Proveedores",
      enero: -650000000,
      febrero: -620000000,
      marzo: -700000000,
      abril: -680000000,
      mayo: -690000000,
      junio: -750000000,
      julio: -730000000,
      agosto: -780000000,
      septiembre: -800000000,
      octubre: -820000000,
      noviembre: -850000000,
      diciembre: -900000000,
      categoria: "Egresos",
      tipo: "Operacional"
    },
    {
      concepto: "Gastos de Personal",
      enero: -280000000,
      febrero: -275000000,
      marzo: -285000000,
      abril: -290000000,
      mayo: -295000000,
      junio: -300000000,
      julio: -305000000,
      agosto: -310000000,
      septiembre: -315000000,
      octubre: -320000000,
      noviembre: -325000000,
      diciembre: -350000000,
      categoria: "Egresos",
      tipo: "Personal"
    },
    {
      concepto: "Gastos Administrativos",
      enero: -120000000,
      febrero: -115000000,
      marzo: -125000000,
      abril: -118000000,
      mayo: -122000000,
      junio: -130000000,
      julio: -128000000,
      agosto: -135000000,
      septiembre: -140000000,
      octubre: -145000000,
      noviembre: -150000000,
      diciembre: -155000000,
      categoria: "Egresos",
      tipo: "Administrativo"
    },
    {
      concepto: "Inversión en Maquinaria",
      enero: -50000000,
      febrero: 0,
      marzo: -80000000,
      abril: 0,
      mayo: -60000000,
      junio: 0,
      julio: -70000000,
      agosto: 0,
      septiembre: -90000000,
      octubre: 0,
      noviembre: -100000000,
      diciembre: 0,
      categoria: "Inversiones",
      tipo: "Activos Fijos"
    },
    {
      concepto: "Intereses Bancarios",
      enero: 15000000,
      febrero: 14000000,
      marzo: 16000000,
      abril: 15500000,
      mayo: 17000000,
      junio: 18000000,
      julio: 19000000,
      agosto: 20000000,
      septiembre: 21000000,
      octubre: 22000000,
      noviembre: 23000000,
      diciembre: 25000000,
      categoria: "Ingresos",
      tipo: "Financiero"
    }
  ]

  const months = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
  ]

  // Formatear números como moneda colombiana
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  // Calcular análisis de flujo de caja
  const calculateCashFlowAnalysis = (data) => {
    if (!data || data.length === 0) return null

    // Separar ingresos y egresos
    const ingresos = data.filter(item => item.categoria === 'Ingresos')
    const egresos = data.filter(item => item.categoria === 'Egresos')
    const inversiones = data.filter(item => item.categoria === 'Inversiones')

    // Calcular totales por mes
    const flujoNetoPorMes = months.map(month => {
      const totalIngresos = ingresos.reduce((sum, row) => sum + (row[month] || 0), 0)
      const totalEgresos = egresos.reduce((sum, row) => sum + (row[month] || 0), 0)
      const totalInversiones = inversiones.reduce((sum, row) => sum + (row[month] || 0), 0)
      return totalIngresos + totalEgresos + totalInversiones
    })

    const totalIngresosAnual = ingresos.reduce((total, row) => 
      total + months.reduce((sum, month) => sum + (row[month] || 0), 0), 0
    )

    const totalEgresosAnual = Math.abs(egresos.reduce((total, row) => 
      total + months.reduce((sum, month) => sum + (row[month] || 0), 0), 0
    ))

    const totalInversionesAnual = Math.abs(inversiones.reduce((total, row) => 
      total + months.reduce((sum, month) => sum + (row[month] || 0), 0), 0
    ))

    const flujoNetoAnual = totalIngresosAnual - totalEgresosAnual - totalInversionesAnual

    // Calcular flujo acumulado
    let flujoAcumulado = 0
    const flujoAcumuladoPorMes = flujoNetoPorMes.map(flujo => {
      flujoAcumulado += flujo
      return flujoAcumulado
    })

    const mejorMes = Math.max(...flujoNetoPorMes)
    const peorMes = Math.min(...flujoNetoPorMes)
    const mejorMesNombre = months[flujoNetoPorMes.indexOf(mejorMes)]
    const peorMesNombre = months[flujoNetoPorMes.indexOf(peorMes)]

    // Liquidez promedio
    const liquidezPromedio = flujoNetoPorMes.reduce((sum, mes) => sum + mes, 0) / 12

    return {
      totalIngresosAnual,
      totalEgresosAnual,
      totalInversionesAnual,
      flujoNetoAnual,
      flujoNetoPorMes,
      flujoAcumuladoPorMes,
      mejorMes,
      peorMes,
      mejorMesNombre,
      peorMesNombre,
      liquidezPromedio,
      margenOperativo: ((totalIngresosAnual - totalEgresosAnual) / totalIngresosAnual * 100).toFixed(1)
    }
  }

  const handleUploadSuccess = () => {
    toast({
      title: "¡Archivo cargado con éxito!",
      description: "Los datos de flujo de caja han sido procesados correctamente.",
      variant: "default"
    })
    setShowUploadModal(false)
    
    // Simular carga de datos desde el archivo
    setCashFlowData(demoData)
    setAnalysisData(calculateCashFlowAnalysis(demoData))
  }

  const handleDownloadTemplate = () => {
    toast({
      title: "Descargando plantilla",
      description: "La plantilla de Excel para flujo de caja se descargará en breve.",
      variant: "default"
    })
  }

  const displayData = cashFlowData.length > 0 ? cashFlowData : demoData
  const displayAnalysis = analysisData || calculateCashFlowAnalysis(demoData)

  return (
    <div className="h-full bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="p-8 space-y-8">
        {/* Header Section */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <Activity className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-800">Configuración de Flujo de Caja</h1>
                <p className="text-slate-600 text-lg">Analiza y gestiona el flujo de efectivo de tu organización</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={handleDownloadTemplate}
                className="flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>Descargar Plantilla</span>
              </Button>
              <Button
                onClick={() => setShowUploadModal(true)}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white flex items-center space-x-2"
              >
                <Upload className="h-4 w-4" />
                <span>Cargar Flujo de Caja</span>
              </Button>
            </div>
          </div>

          {/* Financial Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded p-6 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-600 text-sm font-medium">Ingresos Totales</p>
                  <p className="text-2xl font-bold text-green-900">
                    {formatCurrency(displayAnalysis.totalIngresosAnual)}
                  </p>
                </div>
                <ArrowUpCircle className="h-8 w-8 text-green-600" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-red-50 to-red-100 rounded p-6 border border-red-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-600 text-sm font-medium">Egresos Totales</p>
                  <p className="text-2xl font-bold text-red-900">
                    {formatCurrency(displayAnalysis.totalEgresosAnual)}
                  </p>
                </div>
                <ArrowDownCircle className="h-8 w-8 text-red-600" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded p-6 border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-600 text-sm font-medium">Flujo Neto</p>
                  <p className={`text-2xl font-bold ${displayAnalysis.flujoNetoAnual >= 0 ? 'text-blue-900' : 'text-red-900'}`}>
                    {formatCurrency(displayAnalysis.flujoNetoAnual)}
                  </p>
                </div>
                <Activity className="h-8 w-8 text-blue-600" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded p-6 border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-600 text-sm font-medium">Margen Operativo</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {displayAnalysis.margenOperativo}%
                  </p>
                </div>
                <BarChart3 className="h-8 w-8 text-purple-600" />
              </div>
            </div>
          </div>

          {/* Additional Insights */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded p-6 border border-emerald-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-600 text-sm font-medium">Mejor Mes</p>
                  <p className="text-xl font-bold text-emerald-900 capitalize">
                    {displayAnalysis.mejorMesNombre}
                  </p>
                  <p className="text-sm text-emerald-700">
                    {formatCurrency(displayAnalysis.mejorMes)}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-emerald-600" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded p-6 border border-orange-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-600 text-sm font-medium">Mes Crítico</p>
                  <p className="text-xl font-bold text-orange-900 capitalize">
                    {displayAnalysis.peorMesNombre}
                  </p>
                  <p className="text-sm text-orange-700">
                    {formatCurrency(displayAnalysis.peorMes)}
                  </p>
                </div>
                <TrendingDown className="h-8 w-8 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Cash Flow Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Eye className="h-6 w-6 text-slate-600" />
                <div>
                  <h2 className="text-xl font-semibold text-slate-800">
                    {cashFlowData.length > 0 ? 'Datos de Flujo de Caja Cargados' : 'Vista Previa - Formato de Datos'}
                  </h2>
                  <p className="text-slate-600">
                    {cashFlowData.length > 0 ? 
                      `${cashFlowData.length} conceptos cargados` : 
                      'Ejemplo de cómo se verán los datos una vez cargados'
                    }
                  </p>
                </div>
              </div>
              
              {cashFlowData.length === 0 && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  Vista Previa
                </Badge>
              )}
            </div>
          </div>

          {cashFlowData.length === 0 && (
            <div className="px-6 py-4">
              <Alert className="border-blue-200 bg-blue-50">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  <div className="space-y-2">
                    <p className="font-medium">Esta es una vista previa con datos de ejemplo.</p>
                    <p className="text-sm">Los datos reales aparecerán aquí después de cargar tu archivo Excel de flujo de caja.</p>
                    <div className="text-sm">
                      <strong>Columnas requeridas:</strong> Concepto, Enero-Diciembre, Categoría, Tipo
                    </div>
                    <div className="text-xs text-blue-600">
                      <strong>Nota:</strong> Use valores negativos para egresos e inversiones
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900 sticky left-0 bg-slate-50 z-10">
                    Concepto
                  </th>
                  {months.map(month => (
                    <th key={month} className="px-4 py-4 text-center text-sm font-semibold text-slate-900 min-w-[120px] capitalize">
                      {month}
                    </th>
                  ))}
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900">
                    Categoría
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900">
                    Tipo
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900">
                    Total Anual
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {displayData.map((row, index) => {
                  const totalAnual = months.reduce((sum, month) => sum + (row[month] || 0), 0)
                  const isPositive = totalAnual >= 0
                  return (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-6 py-4 text-sm font-medium text-slate-900 sticky left-0 bg-white hover:bg-slate-50 z-10">
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full ${
                            row.categoria === 'Ingresos' ? 'bg-gradient-to-r from-green-400 to-emerald-500' :
                            row.categoria === 'Egresos' ? 'bg-gradient-to-r from-red-400 to-red-500' :
                            'bg-gradient-to-r from-blue-400 to-blue-500'
                          }`}></div>
                          <span>{row.concepto}</span>
                        </div>
                      </td>
                      {months.map(month => {
                        const value = row[month] || 0
                        const isPositiveValue = value >= 0
                        return (
                          <td key={month} className="px-4 py-4 text-sm text-center">
                            <span className={`font-mono ${isPositiveValue ? 'text-green-700' : 'text-red-700'}`}>
                              {formatCurrency(value)}
                            </span>
                          </td>
                        )
                      })}
                      <td className="px-6 py-4 text-center">
                        <Badge 
                          variant="outline" 
                          className={`
                            ${row.categoria === 'Ingresos' ? 'bg-green-50 text-green-700 border-green-200' : ''}
                            ${row.categoria === 'Egresos' ? 'bg-red-50 text-red-700 border-red-200' : ''}
                            ${row.categoria === 'Inversiones' ? 'bg-blue-50 text-blue-700 border-blue-200' : ''}
                          `}
                        >
                          {row.categoria}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <Badge variant="secondary" className="text-xs">
                          {row.tipo}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-center">
                        <span className={`font-mono ${isPositive ? 'text-green-700' : 'text-red-700'}`}>
                          {formatCurrency(totalAnual)}
                        </span>
                      </td>
                    </motion.tr>
                  )
                })}
              </tbody>
              
              {/* Net Flow Row */}
              <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                <tr>
                  <td className="px-6 py-4 text-sm font-bold text-slate-900 sticky left-0 bg-slate-100 z-10">
                    FLUJO NETO MENSUAL
                  </td>
                  {months.map((month, index) => {
                    const flujoNeto = displayAnalysis.flujoNetoPorMes[index]
                    const isPositive = flujoNeto >= 0
                    return (
                      <td key={month} className="px-4 py-4 text-sm font-bold text-center">
                        <span className={`font-mono ${isPositive ? 'text-green-700' : 'text-red-700'}`}>
                          {formatCurrency(flujoNeto)}
                        </span>
                      </td>
                    )
                  })}
                  <td className="px-6 py-4 text-center">
                    <Badge variant="outline" className="bg-slate-200 text-slate-800 border-slate-400">
                      Resumen
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <Badge variant="outline" className="bg-slate-200 text-slate-800 border-slate-400">
                      -
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-center">
                    <span className={`font-mono text-lg ${displayAnalysis.flujoNetoAnual >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatCurrency(displayAnalysis.flujoNetoAnual)}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Success Message for loaded data */}
        {cashFlowData.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-green-200 p-6">
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-6 w-6 text-green-600" />
              <div>
                <h3 className="text-lg font-semibold text-green-900">Datos Cargados Exitosamente</h3>
                <p className="text-green-700">
                  Se han procesado {cashFlowData.length} conceptos de flujo de caja con análisis financiero completo.
                  Los datos están listos para generar reportes y proyecciones.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        clientId={clientId}
        onSuccess={handleUploadSuccess}
        customColumns={[
          { column_name: "concepto", display_name: "Concepto", is_required: true },
          { column_name: "categoria", display_name: "Categoría", is_required: true },
          { column_name: "tipo", display_name: "Tipo", is_required: true },
          ...months.map(month => ({ 
            column_name: month, 
            display_name: month.charAt(0).toUpperCase() + month.slice(1), 
            is_required: true 
          }))
        ]}
      />
    </div>
  )
} 